import { HttpClient, APIResponseType } from './types';
import { AxiosAdapter } from './AxiosAdapter'; // or FetchAdapter
import { QueryClient } from '@tanstack/react-query';
import queryClient from './queryClient';

class ApiService {
  private httpClient: HttpClient;
  private queryClient: QueryClient;

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient;
    this.queryClient = queryClient;
  }

  // Helper method to generate query key
  private getQueryKey(method: string, url: string, params?: Record<string, any>): any[] {
    return [method, url, params ? { ...params } : {}];
  }

  // Generic method to make GET requests with caching
  async get<T>(url: string, params?: Record<string, any>): Promise<APIResponseType<T>> {
    // Check if this is a blob request and disable caching
    if (params?.responseType === 'blob') {
      return this.httpClient.get<T>(url, params);
    }

    const queryKey = this.getQueryKey('GET', url, params);

    // Return cached data if available, otherwise fetch fresh data
    return this.queryClient.fetchQuery({
      queryKey,
      queryFn: () => this.httpClient.get<T>(url, params),
    });
  }

  // Generic method to make POST requests (with optional caching)
  async post<T>(url: string, data?: any, config?: any, enableCache = true): Promise<APIResponseType<T>> {
    console.log("ApiService post method called with URL:", url);
    console.log("POST data:", data);

    if (enableCache) {
      const queryKey = this.getQueryKey('POST', url, { data, ...config });
      return this.queryClient.fetchQuery({
        queryKey,
        queryFn: () => {
          console.log("Executing POST request via queryFn");
          return this.httpClient.post<T>(url, data, config);
        },
      });
    }
    return this.httpClient.post<T>(url, data, config);
  }

  // Generic method to make PUT requests (with optional caching)
  async put<T>(url: string, data?: any, config?: any, enableCache = true): Promise<APIResponseType<T>> {
    if (enableCache) {
      const queryKey = this.getQueryKey('PUT', url, { data, ...config });
      return this.queryClient.fetchQuery({
        queryKey,
        queryFn: () => this.httpClient.put<T>(url, data, config),
      });
    }
    return this.httpClient.put<T>(url, data, config);
  }

  // Generic method to make DELETE requests
  async delete<T>(url: string, config?: any): Promise<APIResponseType<T>> {
    // Typically we don't cache DELETE requests
    return this.httpClient.delete<T>(url, config);
  }

  // Method to manually invalidate cache for specific endpoints
  invalidateCache(url: string, method: string = 'GET', params?: Record<string, any>) {
    const queryKey = this.getQueryKey(method, url, params);

    console.log("🔄 Invalidating Cache for:", queryKey);

    this.queryClient.removeQueries({ queryKey })
  }

  invalidateCacheByPrefix(url: string, method: string = 'GET', params?: Record<string, any>) {
    const queryKey = this.getQueryKey(method, url);
    console.log("🔄 Invalidating Cache by prefix for:", queryKey);
    this.queryClient.removeQueries({
      queryKey,
      exact: false, // important for partial match
    });
  }

  // Method to clear entire cache
  // clearCache() {
  //   this.queryClient.clear();
  // }
}

// Create an instance of ApiService with the desired HTTP client
const httpClient = new AxiosAdapter(); // or new FetchAdapter()
const apiService = new ApiService(httpClient);

// Add S3 content fetching service
export const fetchS3ContentThroughBackend = async (s3Url: string): Promise<{ content: string; contentType?: string; }> => {
  try {
    const response = await httpClient.post<{ content: string; contentType?: string; }>('api/fetch-s3-content', {
      url: s3Url,
    });

    if (response.body) {
      return response.body;
    } else {
      throw new Error(response.message || 'Failed to fetch S3 content');
    }
  } catch (error) {
    console.error('Error fetching S3 content through backend:', error);
    throw error;
  }
};

export default apiService;